import styled from 'styled-components'
import { Popover, Tooltip, Button, message } from 'antd'
import { isEmpty, isFinite, isNil, toString as str } from 'lodash'
import produce from 'immer'
import cx from 'clsx'
import { createChildListViewComponent } from 'factories/ChildListView'
import entityNames from 'options/entityNames'
import { RED } from 'options/colors'
import { getUserPermission } from 'helpers/auth'
import { validateQuantityReceived, validateQuantityOrdered } from 'helpers/procurement'
import { t } from 'helpers/i18n'
import linkTargets from 'options/linkTargets'
import { tryParseInt, DEBOUNCE } from 'helpers/utils'
import { showError, showClientNotifications } from 'helpers/errors'
import Icon from 'elements/Icon'
import InputNumber from 'elements/InputNumber'
import FormView from 'containers/Orders/Items/FormView'
import PrintLabel from 'containers/PrintLabel'
import Copy from 'containers/Orders/Items/Copy'
import Filter from 'containers/Orders/Items/Filter'

const QuantityReceived = styled.div`
  white-space: nowrap;
  line-height: 32px;
  padding-right: 6px;
  width: 30%;
`

export default createChildListViewComponent({
  filterItems: (self) => (each) => {
    const { supplierId } = self.state.filterDto ?? {}
    const usagePurchase = self.state.filterDto?.usagePurchase ?? 'All'

    if (!isNil(supplierId) && each.supplierId !== supplierId) {
      return false
    }

    if (usagePurchase === 'UsageOnly' && each.quantityUsed === 0 && each.quantityUsed2 === 0) {
      return false
    }

    if (usagePurchase === 'PurchaseOnly' && each.quantityOrdered === 0) {
      return false
    }

    return true
  },
  allowSearching: (self) => true,
  entityName: entityNames.orders,
  childName: 'orderItems',
  createButtonTextId: 'addNonStockItem',
  createButtonLinkTarget: {
    formSize: 'md',
    formComponent: (props) => <FormView {...props} settingsType="order" />,
  },
  primaryLinkTarget: 'orderItemRecord',
  allowCreate: (self) => false,
  allowDelete: (self) => self.props.parentRecord.orderIsEditable && getUserPermission('Purchase') === 'Yes',
  allowCustomize: (self) => true,
  extraRowActions: (self) => (
    <>
      {!self.props.userIsInRoleDistributor &&
        !self.props.userIsInRoleSupplier &&
        getUserPermission('Purchase') !== 'No' && (
          <>
            <Copy
              parentRecord={self.props.parentRecord}
              parentHasUnsavedChanges={self.props.parentHasUnsavedChanges}
              selectedRowKeys={self.state.selectedRowKeys}
            />
            <Button
              onClick={async () => {
                if (self.props.parentHasUnsavedChanges) {
                  message.error(t('saveChangesFirst'))
                  return
                }

                try {
                  self.setState({ movingItems: true })

                  const response = await self.props.moveOrderItems(
                    self.props.parentRecord.id,
                    self.state.selectedRowKeys
                  )

                  showClientNotifications({ response })

                  self.props.onMoveOrderItems()
                } catch (error) {
                  showError({ error })
                } finally {
                  self.setState({ movingItems: false })
                }
              }}
              disabled={isEmpty(self.state.selectedRowKeys)}
              loading={self.state.movingItems}
            >
              {!self.state.movingItems && <Icon type="Send" />} {t('move')}
            </Button>
          </>
        )}
      <PrintLabel
        type="OrderItem"
        items={self.state.selectedRowKeys.map((each) => self.props.items.find((one) => one.id === each))}
        parentHasUnsavedChanges={self.props.parentHasUnsavedChanges}
      />
    </>
  ),
  tableCellComponents: {
    quantityOrdered: (self, item) => {
      const notMultipleOfPackageSize =
        self.props.customer.generalSettings.orderCalculationType === 'Accumulate' &&
        item.quantityOrdered > 0 &&
        item.quantityOrdered % item.packageSize > 0
      const quantityReceived =
        self.props.customer.generalSettings.receivingMode === 'EnteredValueIsDelta'
          ? item.quantityReceived + item.quantityReceivedDelta
          : item.quantityReceived
      const quantityOrderedMin =
        item.quantityIssued > 0 || self.props.customer.generalSettings.allowZeroQuantityOnOrderedItems ? 0 : 1

      return (
        <div className="whitespace-nowrap pr-12">
          <Tooltip title={notMultipleOfPackageSize ? t('errorNotMultipleOfPackageSize') : null}>
            <InputNumber
              key={`${item.id}-${item.timestamp}`}
              value={item.quantityOrdered}
              onChange={async (value) => {
                try {
                  await validateQuantityOrdered(quantityReceived, Math.max(value, quantityOrderedMin))
                  await self.props.onChange(
                    produce(self.props.items, (draft) => {
                      if (isFinite(value)) {
                        const row = draft.find((one) => one.id === item.id)
                        row.quantityOrdered = Math.max(value, quantityOrderedMin)
                        row.populated = false
                      }
                    })
                  )
                } catch (error) {
                  console.warn(error)
                }
              }}
              className={cx({
                'form-field-error': notMultipleOfPackageSize || item.quantityOrdered < quantityReceived,
              })}
              min={0}
              disabled={!self.props.parentRecord.orderIsEditable || getUserPermission('Purchase') !== 'Yes'}
              debounce={DEBOUNCE}
            />
          </Tooltip>{' '}
          <Popover
            title={self.props.customer.moduleSettings.enableInventory ? t('inventoryStatus') : t('itemStatus')}
            content={<span dangerouslySetInnerHTML={{ __html: item.inventoryStatusInfo }} />}
          >
            <span
              className="align-middle"
              style={{ visibility: isEmpty(item.inventoryStatusInfo) ? 'hidden' : 'visible' }}
            >
              <Icon type="Search" />
            </span>
          </Popover>
        </div>
      )
    },
    quantityReceived: (self, item) => {
      const disabled =
        self.props.parentRecord.orderIsReadOnly ||
        item.quantityOrdered <= 0 ||
        !self.props.parentRecord.orderIsReceivable ||
        getUserPermission('ReceiveOrders') !== 'Yes' ||
        item.lockedForCycleCount
      const receivedMoreThanOrdered = item.quantityReceived + item.quantityReceivedDelta > item.quantityOrdered
      const receivedLessThanOrdered = item.quantityReceived + item.quantityReceivedDelta < item.quantityOrdered
      const min = -item.quantityReceived

      return self.props.customer.generalSettings.receivingMode === 'EnteredValueIsDelta' ? (
        <div className="flex">
          <QuantityReceived>{item.quantityReceived}</QuantityReceived>
          <div style={{ width: '70%' }}>
            <InputNumber
              key={`${item.id}-${item.timestamp}`}
              value={item.quantityReceivedDelta}
              onChange={async (value) => {
                try {
                  await validateQuantityReceived(item.quantityOrdered, item.quantityReceived + Math.max(value, min))
                  await self.props.onChange(
                    produce(self.props.items, (draft) => {
                      if (isFinite(value)) {
                        const row = draft.find((one) => one.id === item.id)
                        row.quantityReceivedDelta = Math.max(value, min)
                      }
                    })
                  )
                } catch (error) {
                  console.warn(error)
                }
              }}
              className={cx({
                'form-field-error': receivedMoreThanOrdered,
                'form-field-warning': receivedLessThanOrdered,
              })}
              min={min}
              debounce={DEBOUNCE}
              suffix={
                item.lockedForCycleCount ? (
                  <Tooltip title={t('onhandQuantitiesLockedForCycleCount')}>
                    <Icon type="Lock" color={RED} />
                  </Tooltip>
                ) : null
              }
              disabled={disabled}
            />
          </div>
        </div>
      ) : (
        <InputNumber
          key={`${item.id}-${item.timestamp}`}
          value={item.quantityReceived}
          onChange={async (value) => {
            try {
              await validateQuantityReceived(item.quantityOrdered, value)
              await self.props.onChange(
                produce(self.props.items, (draft) => {
                  if (isFinite(value)) {
                    const row = draft.find((one) => one.id === item.id)
                    row.quantityReceived = value
                  }
                })
              )
            } catch (error) {
              console.warn(error)
            }
          }}
          className={cx({
            'form-field-error': receivedMoreThanOrdered,
            'form-field-warning': receivedLessThanOrdered,
          })}
          min={0}
          debounce={DEBOUNCE}
          suffix={
            item.lockedForCycleCount ? (
              <Tooltip title={t('onhandQuantitiesLockedForCycleCount')}>
                <Icon type="Lock" color={RED} />
              </Tooltip>
            ) : null
          }
          disabled={disabled}
        />
      )
    },
    source: (self, item) => {
      const source = str(item?.source)

      if (!source.match(/(REQ|RFQ)(\d+)/)) {
        return source
      }

      return source
        .match(/(REQ|RFQ)(\d+)/g)
        .map((each) => {
          const match = each.match(/(REQ|RFQ)(\d+)/)

          return (
            <a
              key={each}
              onClick={() =>
                self.setState({
                  editDrawerLinkTarget: match[1] === 'RFQ' ? linkTargets.rfqRecord : linkTargets.requisitionRecord,
                  editDrawerLinkTargetText: match[0],
                  editDrawerLinkTargetRecord:
                    match[1] === 'RFQ'
                      ? { rfqId: tryParseInt(match[2], 0) }
                      : { requisitionId: tryParseInt(match[2], 0) },
                  editDrawerVisible: true,
                  editDrawerSaving: false,
                  editDrawerLinkTargetReadOnly: true,
                })
              }
            >
              {each}
            </a>
          )
        })
        .reduce((acc, each) => [acc, ', ', each])
    },
  },
})(Filter)
